import React from 'react';
import { 
  Sparkles, 
  Leaf, 
  ShieldCheck, 
  Droplets, 
  Package, 
  Flame, 
  Heart,
  Stethoscope,
  Sun,
  Coffee,
  Activity,
  Pill,
  Zap,
  ArrowLeft, 
  ArrowRight 
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';
import ProductCarouselRow from './ProductCarouselRow';

const ICON_MAP = {
  Leaf,
  Droplets,
  Sparkles,
  ShieldCheck,
  Package,
  Flame,
  Heart,
  Stethoscope,
  Sun,
  Coffee,
  Activity,
  Pill,
  Zap
};

// Theme presets for badges and icons
const THEME_BADGES = {
  emerald: { bg: 'bg-emerald-100 text-emerald-950', icon: 'text-emerald-700' },
  teal: { bg: 'bg-teal-100 text-teal-950', icon: 'text-teal-700' },
  amber: { bg: 'bg-amber-100 text-amber-950', icon: 'text-amber-700' },
  stone: { bg: 'bg-stone-100 text-stone-900', icon: 'text-stone-700' },
  green: { bg: 'bg-green-100 text-green-950', icon: 'text-green-700' },
  rose: { bg: 'bg-rose-100 text-rose-950', icon: 'text-rose-700' },
  purple: { bg: 'bg-purple-100 text-purple-950', icon: 'text-purple-700' },
  sky: { bg: 'bg-sky-100 text-sky-950', icon: 'text-sky-700' }
};

const FeaturedProducts = ({ onShopClick, onSelectCategory }) => {
  const { lang, isRtl } = useLanguage();
  const { products = [], cmsContent, categories = [] } = useStore();

  // 1. First Row: Featured Products (جن پر نمایاں کا ٹک لگا ہو)
  const featuredProducts = products.filter(p => p.featured === true);
  // Fallback if none are ticked yet
  const displayedFeatured = featuredProducts.length > 0 
    ? featuredProducts 
    : products.filter(p => p.rating >= 4.9);

  const handleCategoryNav = (categoryId) => {
    if (onSelectCategory) {
      onSelectCategory(categoryId);
    } else if (onShopClick) {
      onShopClick();
    }
  };

  // Filter categories enabled for home slider rows and sort by order
  const activeHomeCategories = categories
    .filter(c => c.showRowOnHome !== false && c.showOnHome !== false)
    .sort((a, b) => (Number(a.order) || 99) - (Number(b.order) || 99));

  return (
    <section className="py-12 sm:py-16 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        
        {/* Main Section Header Banner */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 pb-6 border-b border-gray-100">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-950 text-xs font-bold font-title mb-2.5">
              <Sparkles className="w-3.5 h-3.5 text-emerald-700" />
              <span>{lang === 'ur' ? 'خالص اور مستند نباتاتی ادویات' : '100% Pure & Authentic Formulations'}</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-black text-pansar-950 font-title">
              {lang === 'ur' ? (cmsContent?.sectionTitles?.featuredTitleUrdu || 'ادویات و مصنوعات کا جامع ذخیرہ') : (cmsContent?.sectionTitles?.featuredTitleEn || 'Complete Herbal & Formulation Store')}
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 font-urdu mt-1.5 max-w-2xl">
              {lang === 'ur' ? (cmsContent?.sectionTitles?.featuredSubtitleUrdu || 'حکیم مولانا سید عبدالوہاب شاہ شیرازی کے مصدقہ اور آزمودہ قدرتی نسخہ جات، کیٹیگری وائز ترتیب شدہ') : (cmsContent?.sectionTitles?.featuredSubtitleEn || 'Highest rated herbal formulas for vitality, digestion, and total body wellness categorized for easy access')}
            </p>
          </div>

          {/* Browse All Products Button */}
          <button
            type="button"
            onClick={onShopClick}
            className="self-start md:self-auto px-5 py-2.5 rounded-2xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs flex items-center gap-2 font-urdu transition shadow-sm hover:shadow-md cursor-pointer"
          >
            <span>{lang === 'ur' ? 'تمام ادویات و فلٹرز دیکھیں' : 'Browse All Products & Filters'}</span>
            {isRtl ? <ArrowLeft className="w-4 h-4 text-amber-300" /> : <ArrowRight className="w-4 h-4 text-amber-300" />}
          </button>
        </div>

        {/* ========================================================= */}
        {/* ROW 1: نمایاں ادویات (Featured Products) */}
        {/* ========================================================= */}
        <ProductCarouselRow
          title={lang === 'ur' ? 'نمایاں ادویات و انتخابِ خاص' : 'Featured & Bestselling Formulations'}
          subtitle={lang === 'ur' ? 'وہ منتخب ادویات اور فارمولیشنز جو سب سے زیادہ خریدی اور پسند کی جاتی ہیں' : 'Handpicked bestselling herbal formulas tested for proven efficacy'}
          tag={lang === 'ur' ? 'انتخابِ خاص (Featured)' : 'Featured Selection'}
          icon={Sparkles}
          badgeBg="bg-amber-100 text-amber-950"
          iconColor="text-amber-600"
          products={displayedFeatured}
          onViewAll={onShopClick}
          autoScrollInterval={2000}
          categoryId="all"
        />

        {/* ========================================================= */}
        {/* DYNAMIC CATEGORY ROWS (تمام فعال کیٹیگریز کے سلائیڈرز) */}
        {/* ========================================================= */}
        {activeHomeCategories.map((cat, idx) => {
          let catProducts = products.filter(p => p.category === cat.id);

          // Smart fallback matching for initial/legacy categories
          if (catProducts.length === 0) {
            if (cat.id === 'raw-herbs') {
              catProducts = products.filter(p => 
                p.category === 'raw-herbs' || 
                p.category?.toLowerCase().includes('herb') ||
                p.nameUrdu?.includes('بوٹی') ||
                p.nameUrdu?.includes('بیج') ||
                p.nameUrdu?.includes('کلونجی') ||
                p.nameUrdu?.includes('اسبغو') ||
                p.nameUrdu?.includes('زعفران')
              );
            } else if (cat.id === 'majun-jawarish') {
              catProducts = products.filter(p => 
                p.category === 'majun-jawarish' || 
                p.category?.toLowerCase().includes('majun') ||
                p.category?.toLowerCase().includes('jawarish') ||
                p.nameUrdu?.includes('معجون') ||
                p.nameUrdu?.includes('جوارش') ||
                p.nameUrdu?.includes('خمیرہ') ||
                p.nameUrdu?.includes('اطریفل') ||
                p.nameUrdu?.includes('لبوب')
              );
            } else if (cat.id === 'kushta-safoof') {
              catProducts = products.filter(p => 
                p.category === 'kushta-safoof' || 
                p.category?.toLowerCase().includes('safoof') ||
                p.category?.toLowerCase().includes('kushta') ||
                p.category?.toLowerCase().includes('powder') ||
                p.nameUrdu?.includes('سفوف') ||
                p.nameUrdu?.includes('کشتہ') ||
                p.nameUrdu?.includes('پاؤڈر') ||
                p.nameUrdu?.includes('تریاق')
              );
            } else if (cat.id === 'oils-distillates') {
              catProducts = products.filter(p => 
                p.category === 'oils-distillates' || 
                p.category?.toLowerCase().includes('oil') ||
                p.category?.toLowerCase().includes('distillate') ||
                p.nameUrdu?.includes('عرق') ||
                p.nameUrdu?.includes('روغن') ||
                p.nameUrdu?.includes('تیل')
              );
            } else if (cat.id === 'syrups-company') {
              catProducts = products.filter(p => 
                p.category === 'syrups-company' || 
                p.nameUrdu?.includes('شربت') ||
                p.nameUrdu?.includes('سلاجیت')
              );
            }
          }

          // If no products in this category yet, skip rendering empty carousel
          if (catProducts.length === 0) return null;

          const IconComp = ICON_MAP[cat.icon] || Leaf;
          
          // Theme badge styling
          const themeKeys = ['emerald', 'amber', 'stone', 'teal', 'green', 'purple', 'sky', 'rose'];
          const themeKey = themeKeys[idx % themeKeys.length];
          const badgeTheme = THEME_BADGES[themeKey] || THEME_BADGES.emerald;

          return (
            <ProductCarouselRow
              key={cat.id}
              title={lang === 'ur' ? `کیٹیگری: ${cat.nameUrdu}` : `Category: ${cat.nameEn || cat.nameUrdu}`}
              subtitle={lang === 'ur' ? (cat.taglineUrdu || 'خالص و آزمودہ ہربل ادویات') : (cat.taglineEn || 'Pure and potent herbal formulations')}
              tag={lang === 'ur' ? cat.nameUrdu : (cat.nameEn || cat.nameUrdu)}
              icon={IconComp}
              badgeBg={badgeTheme.bg}
              iconColor={badgeTheme.icon}
              products={catProducts}
              onViewAll={() => handleCategoryNav(cat.id)}
              autoScrollInterval={2000}
              categoryId={cat.id}
            />
          );
        })}

      </div>
    </section>
  );
};

export default FeaturedProducts;
